import { useState, useEffect, useCallback } from "react";
import { auth, db, secondaryAuth } from "./firebase";
import {
  signInWithEmailAndPassword, signOut,
  createUserWithEmailAndPassword, onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import {
  createBankAccount, getAccount, getAllAccounts,
  deposit, withdraw, getHistory
} from "./bankService";
import "./bank.css";

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast toast-${type}`}><span>{type === "success" ? "✓" : "!"}</span>{msg}</div>;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const go = async () => {
    setErr("");
    if (!email || !pass) { setErr("Please enter your email and password."); return; }
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, pass);
      const snap = await getDoc(doc(db, "users", cred.user.uid));
      if (!snap.exists()) throw new Error("no-profile");
      onLogin({ uid: cred.user.uid, email: cred.user.email, ...snap.data() });
    } catch (e) {
      setErr(
        e.code === "auth/invalid-credential" ? "Invalid email or password." :
        e.code === "auth/too-many-requests" ? "Too many attempts. Try again later." :
        e.message === "no-profile" ? "Account not found. Contact your administrator." :
        "Authentication failed."
      );
    }
    setLoading(false);
  };

  return (
    <div className="login-wrap">
      <div className="login-brand">
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ width: 36, height: 36, background: "var(--gold)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>🏛</div>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700, color: "white" }}>SecurBank</span>
        </div>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold-light)", marginBottom: 18 }}>Trusted Banking Portal</p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 44, fontWeight: 600, color: "white", lineHeight: 1.15, marginBottom: 18 }}>Banking that<br />works for you.</h1>
          <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.42)", lineHeight: 1.75, maxWidth: 330 }}>Secure, real-time transactions. Role-based access. Professional-grade infrastructure.</p>
        </div>
        <div style={{ display: "flex", gap: 36 }}>
          {[["256-bit", "Encryption"], ["99.9%", "Uptime"], ["Real-time", "Sync"]].map(([v, l]) => (
            <div key={l}>
              <p style={{ fontFamily: "'Cormorant Garamond'", fontSize: 22, fontWeight: 700, color: "white" }}>{v}</p>
              <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.32)", marginTop: 2 }}>{l}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="login-form-side">
        <div style={{ width: "100%", maxWidth: 370 }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: "var(--navy)", marginBottom: 7 }}>Sign in to your account</h2>
            <p style={{ fontSize: 13.5, color: "var(--text2)" }}>Enter your credentials to access the portal.</p>
          </div>
          {err && <div className="err">⚠ {err}</div>}
          <div className="il">
            <label>Email Address</label>
            <input type="email" placeholder="you@securbank.in" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && go()} />
          </div>
          <div className="il">
            <label>Password</label>
            <div style={{ position: "relative" }}>
              <input type={show ? "text" : "password"} placeholder="••••••••" value={pass} onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === "Enter" && go()} style={{ paddingRight: 52 }} />
              <button onClick={() => setShow(!show)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text3)", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em" }}>
                {show ? "HIDE" : "SHOW"}
              </button>
            </div>
          </div>
          <button className="btn-primary" onClick={go} disabled={loading} style={{ width: "100%", padding: 13, fontSize: 14.5, marginTop: 4 }}>
            {loading ? "Verifying..." : "Sign In →"}
          </button>
          <div style={{ marginTop: 22, padding: "14px 16px", background: "white", borderRadius: 10, border: "1px solid var(--border2)" }}>
            <p style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" }}>Portal Access</p>
            <p style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.65 }}>Access is granted by your bank administrator. Contact your branch manager to get your login credentials.</p>
          </div>
          <p style={{ textAlign: "center", fontSize: 11.5, color: "var(--text3)", marginTop: 20 }}>🔒 Secured by Firebase Authentication</p>
        </div>
      </div>
    </div>
  );
}

function TxModal({ account, type, currentUserUid, onClose, onDone, toast }) {
  const [amount, setAmount] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const isD = type === "deposit";

  const go = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) { toast("Enter a valid amount.", "error"); return; }
    if (pin.length !== 4) { toast("PIN must be 4 digits.", "error"); return; }
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "users", currentUserUid));
      if (!snap.exists()) { toast("User not found.", "error"); setLoading(false); return; }
      const storedPin = snap.data().transactionPin;
      if (!storedPin) { toast("No transaction PIN set. Contact your administrator.", "error"); setLoading(false); return; }
      if (pin !== storedPin) { toast("Incorrect transaction PIN.", "error"); setLoading(false); return; }
      if (isD) { await deposit(account.accountId, Number(amount)); }
      else { await withdraw(account.accountId, Number(amount)); }
      toast(`${isD ? "Deposit" : "Withdrawal"} of ₹${Number(amount).toLocaleString("en-IN")} successful.`, "success");
      onDone(); onClose();
    } catch (e) { toast(e.message || "Transaction failed.", "error"); }
    setLoading(false);
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 390 }}>
        <div style={{ height: 3, background: isD ? "var(--green)" : "var(--red)", borderRadius: "3px 3px 0 0", margin: "-32px -32px 26px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 20 }}>
          <div>
            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: isD ? "var(--green)" : "var(--red)", marginBottom: 3 }}>{isD ? "Credit Transaction" : "Debit Transaction"}</p>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, color: "var(--navy)" }}>{account.ownerName}</h3>
            <p style={{ fontSize: 11.5, color: "var(--text3)", fontFamily: "'JetBrains Mono'", marginTop: 2 }}>{account.accountId}</p>
          </div>
          <button onClick={onClose} style={{ background: "var(--cream2)", border: "none", borderRadius: 7, width: 30, height: 30, fontSize: 17, color: "var(--text2)", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ background: "var(--cream)", borderRadius: 9, padding: "12px 16px", marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12.5, color: "var(--text2)" }}>Available Balance</span>
          <span style={{ fontFamily: "'JetBrains Mono'", fontSize: 17, color: "var(--navy)", fontWeight: 500 }}>₹{account.balance.toLocaleString("en-IN")}</span>
        </div>
        <div className="il">
          <label>Amount (₹)</label>
          <input type="number" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} style={{ fontFamily: "'JetBrains Mono'", fontSize: 19 }} autoFocus />
        </div>
        <div className="il">
          <label>Transaction PIN</label>
          <input type="password" placeholder="• • • •" maxLength={4} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ""))} style={{ fontFamily: "'JetBrains Mono'", fontSize: 20, letterSpacing: "0.5em", textAlign: "center" }} />
          <p style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 5 }}>Enter the 4-digit PIN set by your administrator.</p>
        </div>
        <div style={{ display: "flex", gap: 9, marginTop: 4 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button onClick={go} disabled={loading} style={{ flex: 2, padding: 12, borderRadius: 8, fontWeight: 600, fontSize: 13.5, cursor: "pointer", border: `1.5px solid ${isD ? "rgba(26,122,74,0.25)" : "rgba(176,58,46,0.25)"}`, background: isD ? "var(--green-bg)" : "var(--red-bg)", color: isD ? "var(--green)" : "var(--red)" }}>
            {loading ? "Verifying PIN..." : `Confirm ${isD ? "Deposit" : "Withdrawal"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onDone, toast }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", accountId: "", balance: "", pin: "", pinConfirm: "" });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const go = async () => {
    if (!form.pin || form.pin.length !== 4) { toast("Transaction PIN must be exactly 4 digits.", "error"); return; }
    if (form.pin !== form.pinConfirm) { toast("PINs do not match.", "error"); return; }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(secondaryAuth, form.email, form.password);
      await setDoc(doc(db, "users", cred.user.uid), {
        name: form.name, email: form.email, role: form.role,
        accountId: form.accountId || null,
        transactionPin: form.pin,
        createdAt: new Date().toISOString(),
      });
      if (form.accountId && form.balance) {
        await createBankAccount(form.accountId, form.name, Number(form.balance), cred.user.uid);
      }
      toast(`User "${form.name}" created successfully.`, "success");
      onDone(); onClose();
    } catch (e) {
      toast(e.code === "auth/email-already-in-use" ? "This email is already registered." : e.message || "Failed to create user.", "error");
    }
    setLoading(false);
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 24 }}>
          <div>
            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 3 }}>Admin · New User</p>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "var(--navy)" }}>Create User Account</h3>
          </div>
          <button onClick={onClose} style={{ background: "var(--cream2)", border: "none", borderRadius: 7, width: 30, height: 30, fontSize: 17, color: "var(--text2)", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ display: "flex", background: "var(--cream2)", borderRadius: 8, padding: 3, marginBottom: 22 }}>
          {["Login Details", "Bank & PIN"].map((l, i) => (
            <button key={l} onClick={() => i === 0 && setStep(1)} style={{ flex: 1, padding: 8, borderRadius: 6, border: "none", fontSize: 13, fontWeight: 500, background: step === i + 1 ? "white" : "transparent", color: step === i + 1 ? "var(--navy)" : "var(--text2)", boxShadow: step === i + 1 ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.15s", cursor: i === 0 ? "pointer" : "default" }}>{l}</button>
          ))}
        </div>
        {step === 1 && <>
          <div className="il"><label>Full Name *</label><input placeholder="e.g. Rahul Sharma" value={form.name} onChange={e => set("name", e.target.value)} /></div>
          <div className="il"><label>Email Address *</label><input type="email" placeholder="user@securbank.in" value={form.email} onChange={e => set("email", e.target.value)} /></div>
          <div className="il"><label>Password *</label><input type="password" placeholder="Min. 6 characters" value={form.password} onChange={e => set("password", e.target.value)} /></div>
          <div className="il"><label>Role</label>
            <select value={form.role} onChange={e => set("role", e.target.value)}>
              <option value="user">Customer</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          <button className="btn-primary" onClick={() => { if (!form.name || !form.email || !form.password) { toast("Fill all required fields.", "error"); return; } if (form.password.length < 6) { toast("Password must be at least 6 characters.", "error"); return; } setStep(2); }} style={{ width: "100%" }}>Next: Bank & PIN →</button>
        </>}
        {step === 2 && <>
          <div className="il"><label>Account ID</label><input placeholder="e.g. ACC005" value={form.accountId} onChange={e => set("accountId", e.target.value)} /></div>
          <div className="il"><label>Initial Deposit (₹)</label><input type="number" placeholder="0.00" value={form.balance} onChange={e => set("balance", e.target.value)} /></div>
          <div style={{ height: 1, background: "var(--border2)", margin: "4px 0 18px" }} />
          <div style={{ padding: "12px 14px", background: "rgba(13,27,62,0.03)", borderRadius: 9, border: "1px solid var(--border2)", marginBottom: 16 }}>
            <p style={{ fontSize: 11.5, fontWeight: 600, color: "var(--navy)", marginBottom: 3 }}>🔐 Transaction PIN</p>
            <p style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6 }}>Set a 4-digit PIN for this user. They will need this PIN to authorize every deposit and withdrawal.</p>
          </div>
          <div className="il"><label>Set Transaction PIN *</label>
            <input type="password" placeholder="• • • •" maxLength={4} value={form.pin} onChange={e => set("pin", e.target.value.replace(/\D/g, ""))} style={{ fontFamily: "'JetBrains Mono'", fontSize: 20, letterSpacing: "0.5em", textAlign: "center" }} />
          </div>
          <div className="il"><label>Confirm PIN *</label>
            <input type="password" placeholder="• • • •" maxLength={4} value={form.pinConfirm} onChange={e => set("pinConfirm", e.target.value.replace(/\D/g, ""))} style={{ fontFamily: "'JetBrains Mono'", fontSize: 20, letterSpacing: "0.5em", textAlign: "center" }} />
            {form.pin && form.pinConfirm && (
              <p style={{ fontSize: 12, color: form.pin === form.pinConfirm ? "var(--green)" : "var(--red)", marginTop: 5 }}>
                {form.pin === form.pinConfirm ? "✓ PINs match." : "PINs do not match."}
              </p>
            )}
          </div>
          <div style={{ display: "flex", gap: 9, marginTop: 4 }}>
            <button className="btn-outline" onClick={() => setStep(1)} style={{ flex: 1 }}>← Back</button>
            <button className="btn-primary" onClick={go} disabled={loading} style={{ flex: 2 }}>{loading ? "Creating..." : "Create User"}</button>
          </div>
        </>}
      </div>
    </div>
  );
}

function AdminDashboard({ user, onLogout }) {
  const [view, setView] = useState("overview");
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [modal, setModal] = useState(null);
  const [toastData, setToastData] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useCallback((msg, type = "success") => setToastData({ msg, type }), []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try { setAccounts(await getAllAccounts()); }
    catch { toast("Failed to load accounts.", "error"); }
    setLoading(false);
  }, [toast]);

  const openAccount = async (acc) => {
    setSelected(acc); setView("detail");
    try {
      const [h, a] = await Promise.all([getHistory(acc.accountId), getAccount(acc.accountId)]);
      setHistory(h); setSelected(a);
    } catch { toast("Failed to load details.", "error"); }
  };

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const total = accounts.reduce((s, a) => s + a.balance, 0);
  const avg = accounts.length ? Math.round(total / accounts.length) : 0;

  const navItems = [
    { id: "overview", icon: "▦", label: "Overview" },
    { id: "accounts", icon: "◈", label: "All Accounts" },
    ...(selected ? [{ id: "detail", icon: "◉", label: "Account Detail" }] : []),
    { id: "users", icon: "⊕", label: "User Management" },
  ];

  return (
    <div className="admin-layout">
      {toastData && <Toast msg={toastData.msg} type={toastData.type} onClose={() => setToastData(null)} />}
      {modal === "createUser" && <CreateUserModal onClose={() => setModal(null)} onDone={fetchAccounts} toast={toast} />}
      {(modal === "deposit" || modal === "withdraw") && selected && (
        <TxModal account={selected} type={modal} currentUserUid={user.uid} onClose={() => setModal(null)} onDone={() => openAccount(selected)} toast={toast} />
      )}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
            <div style={{ width: 30, height: 30, background: "var(--gold)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🏛</div>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "white" }}>SecurBank</span>
          </div>
          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.22)", marginLeft: 40, textTransform: "uppercase", letterSpacing: "0.09em" }}>Admin Portal</p>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(n => (
            <button key={n.id} className={`nav-btn ${view === n.id ? "active" : ""}`} onClick={() => setView(n.id)}>
              <span style={{ fontSize: 14, width: 18, textAlign: "center", opacity: 0.7 }}>{n.icon}</span>{n.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div style={{ padding: "11px 13px", borderRadius: 9, background: "rgba(255,255,255,0.05)", marginBottom: 10 }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: "white", marginBottom: 1 }}>{user.name}</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.32)" }}>{user.email}</p>
            <span className="badge badge-admin" style={{ marginTop: 7 }}>Admin</span>
          </div>
          <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 7, background: "none", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.4)", fontSize: 12.5, cursor: "pointer", width: "100%" }}>↩ Sign Out</button>
        </div>
      </aside>
      <div className="admin-main">
        <div className="topbar">
          <div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700, color: "var(--navy)" }}>
              {view === "overview" ? `Welcome back, ${user.name.split(" ")[0]}` : view === "accounts" ? "All Accounts" : view === "detail" ? selected?.ownerName || "Account Detail" : "User Management"}
            </h1>
            <p style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 1 }}>{new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
          </div>
          <div style={{ display: "flex", gap: 9 }}>
            {view === "detail" && selected && <>
              <button className="btn-green" onClick={() => setModal("deposit")}>↑ Deposit</button>
              <button className="btn-red" onClick={() => setModal("withdraw")}>↓ Withdraw</button>
            </>}
            {view !== "detail" && <button className="btn-primary" onClick={() => setModal("createUser")}>+ New User</button>}
          </div>
        </div>
        <div className="page-content">
          {view === "overview" && <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 24 }}>
              {[
                { l: "Total Accounts", v: accounts.length, sub: "Registered accounts", i: "◈" },
                { l: "Total Assets", v: `₹${total.toLocaleString("en-IN")}`, sub: "Under management", i: "₹" },
                { l: "Avg. Balance", v: `₹${avg.toLocaleString("en-IN")}`, sub: "Per account", i: "~" },
              ].map(s => (
                <div key={s.l} className="stat-card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 14 }}>
                    <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text3)" }}>{s.l}</p>
                    <div style={{ width: 30, height: 30, borderRadius: 7, background: "var(--cream2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "var(--navy)" }}>{s.i}</div>
                  </div>
                  <p style={{ fontFamily: "'JetBrains Mono'", fontSize: 22, fontWeight: 500, color: "var(--navy)", marginBottom: 3 }}>{s.v}</p>
                  <p style={{ fontSize: 11.5, color: "var(--text3)" }}>{s.sub}</p>
                </div>
              ))}
            </div>
            <div className="card">
              <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond'", fontSize: 17, color: "var(--navy)" }}>Recent Accounts</h3>
                <button onClick={() => setView("accounts")} style={{ fontSize: 12.5, color: "var(--gold)", background: "none", border: "none", fontWeight: 600, cursor: "pointer" }}>View all →</button>
              </div>
              {loading ? [1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 56, margin: "6px 18px" }} />) :
                accounts.slice(0, 5).map(acc => (
                  <div key={acc.accountId} className="acc-row" onClick={() => openAccount(acc)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="avatar">{(acc.ownerName || "?")[0].toUpperCase()}</div>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 13.5 }}>{acc.ownerName}</p>
                        <p style={{ fontSize: 11.5, color: "var(--text3)", fontFamily: "'JetBrains Mono'" }}>{acc.accountId}</p>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontFamily: "'JetBrains Mono'", fontSize: 14.5, color: "var(--navy)", fontWeight: 500 }}>₹{acc.balance.toLocaleString("en-IN")}</p>
                      <span className="badge badge-active" style={{ marginTop: 4 }}>Active</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </>}
          {view === "accounts" && (
            <div className="card">
              {loading ? [1,2,3].map(i => <div key={i} className="shimmer" style={{ height: 60, margin: "6px 18px" }} />) :
                accounts.length === 0
                  ? <div style={{ textAlign: "center", padding: "56px 0", color: "var(--text3)" }}>
                      <p style={{ fontSize: 32, marginBottom: 10 }}>◈</p>
                      <p style={{ fontSize: 15, color: "var(--text2)", fontWeight: 600 }}>No accounts yet</p>
                      <p style={{ fontSize: 13, marginTop: 4 }}>Click "New User" to create the first account.</p>
                    </div>
                  : accounts.map(acc => (
                    <div key={acc.accountId} className={`acc-row ${selected?.accountId === acc.accountId ? "sel" : ""}`} onClick={() => openAccount(acc)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="avatar">{(acc.ownerName || "?")[0].toUpperCase()}</div>
                        <div>
                          <p style={{ fontWeight: 600, fontSize: 13.5 }}>{acc.ownerName}</p>
                          <p style={{ fontSize: 11.5, color: "var(--text3)", fontFamily: "'JetBrains Mono'" }}>{acc.accountId}</p>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{ textAlign: "right" }}>
                          <p style={{ fontFamily: "'JetBrains Mono'", fontSize: 14.5, color: "var(--navy)", fontWeight: 500 }}>₹{acc.balance.toLocaleString("en-IN")}</p>
                          <span className="badge badge-active" style={{ marginTop: 4 }}>Active</span>
                        </div>
                        <span style={{ color: "var(--text3)", fontSize: 18 }}>›</span>
                      </div>
                    </div>
                  ))
              }
            </div>
          )}
          {view === "detail" && selected && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div style={{ background: "var(--navy)", borderRadius: 14, padding: "28px 32px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.025)" }} />
                <p style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.32)", marginBottom: 7 }}>Account Balance</p>
                <p style={{ fontFamily: "'JetBrains Mono'", fontSize: 42, fontWeight: 500, color: "white", marginBottom: 22 }}>₹{selected.balance.toLocaleString("en-IN")}</p>
                <div style={{ display: "flex", gap: 36 }}>
                  {[["Account Holder", selected.ownerName, false], ["Account ID", selected.accountId, true], ["Status", "Active", false]].map(([l, v, mono]) => (
                    <div key={l}>
                      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", marginBottom: 4 }}>{l}</p>
                      <p style={{ fontSize: 13.5, fontWeight: 600, color: "white", fontFamily: mono ? "'JetBrains Mono'" : "inherit" }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border2)" }}>
                  <h3 style={{ fontFamily: "'Cormorant Garamond'", fontSize: 17, color: "var(--navy)" }}>Transaction History</h3>
                </div>
                {history.length === 0
                  ? <div style={{ padding: "36px 22px", color: "var(--text3)", fontSize: 13 }}>No transactions yet.</div>
                  : history.map((h, i) => {
                      const isC = h.includes("Deposited") || h.includes("created");
                      const isDb = h.includes("Withdrew");
                      return (
                        <div key={i} className="tx-row">
                          <div className="tx-icon" style={{ background: isC ? "var(--green-bg)" : isDb ? "var(--red-bg)" : "var(--cream2)", color: isC ? "var(--green)" : isDb ? "var(--red)" : "var(--text2)" }}>
                            {isC ? "↑" : isDb ? "↓" : "·"}
                          </div>
                          <p style={{ flex: 1, fontSize: 13, color: "var(--text)" }}>{h}</p>
                          <span className="badge" style={{ background: isC ? "var(--green-bg)" : isDb ? "var(--red-bg)" : "var(--cream2)", color: isC ? "var(--green)" : isDb ? "var(--red)" : "var(--text2)", border: "none" }}>
                            {isC ? "Credit" : isDb ? "Debit" : "System"}
                          </span>
                        </div>
                      );
                    })
                }
              </div>
            </div>
          )}
          {view === "users" && (
            <div className="card">
              <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border2)" }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond'", fontSize: 17, color: "var(--navy)", marginBottom: 3 }}>User Management</h3>
                <p style={{ fontSize: 13, color: "var(--text3)" }}>Create users, assign bank accounts, and set transaction PINs.</p>
              </div>
              <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ padding: 16, background: "var(--cream)", borderRadius: 9, border: "1px solid var(--border2)" }}>
                  <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.7 }}>
                    View all registered users in <strong>Firebase Console → Authentication → Users</strong>.<br />
                    Roles, account IDs, and PINs are stored in <strong>Firestore → users</strong>.<br />
                    Bank accounts and transactions are in <strong>Firestore → accounts / transactions</strong>.
                  </p>
                </div>
                <button className="btn-primary" onClick={() => setModal("createUser")}>+ Create New User & Account</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UserDashboard({ user, onLogout }) {
  const [account, setAccount] = useState(null);
  const [history, setHistory] = useState([]);
  const [modal, setModal] = useState(null);
  const [toastData, setToastData] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useCallback((msg, type = "success") => setToastData({ msg, type }), []);

  const fetchAccount = useCallback(async () => {
    if (!user.accountId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [a, h] = await Promise.all([getAccount(user.accountId), getHistory(user.accountId)]);
      setAccount(a); setHistory(h);
    } catch { toast("Failed to load your account.", "error"); }
    setLoading(false);
  }, [user.accountId, toast]);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)" }}>
      {toastData && <Toast msg={toastData.msg} type={toastData.type} onClose={() => setToastData(null)} />}
      {(modal === "deposit" || modal === "withdraw") && account && (
        <TxModal account={account} type={modal} currentUserUid={user.uid} onClose={() => setModal(null)} onDone={fetchAccount} toast={toast} />
      )}
      <nav className="user-nav">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, background: "var(--gold)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>🏛</div>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "white" }}>SecurBank</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: "white" }}>{user.name}</p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.38)" }}>{user.email}</p>
          </div>
          <button onClick={onLogout} style={{ padding: "7px 14px", borderRadius: 7, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)", fontSize: 12.5, cursor: "pointer" }}>Sign Out</button>
        </div>
      </nav>
      <div className="user-content">
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>Good day, {user.name.split(" ")[0]}</h1>
          <p style={{ fontSize: 13.5, color: "var(--text2)" }}>{new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
        {loading ? <>
          <div className="shimmer" style={{ height: 180, marginBottom: 18 }} />
          <div className="shimmer" style={{ height: 300 }} />
        </> : !account ? (
          <div className="card" style={{ padding: "48px 32px", textAlign: "center" }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>◈</p>
            <p style={{ fontSize: 16, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>No account linked</p>
            <p style={{ fontSize: 13.5, color: "var(--text3)", lineHeight: 1.65 }}>Your bank account has not been set up yet.<br />Please contact your branch administrator.</p>
          </div>
        ) : <>
          <div style={{ background: "var(--navy)", borderRadius: 16, padding: "28px 32px", marginBottom: 18, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -30, right: -20, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.025)" }} />
            <p style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.32)", marginBottom: 7 }}>Available Balance</p>
            <p style={{ fontFamily: "'JetBrains Mono'", fontSize: 44, fontWeight: 500, color: "white", marginBottom: 22 }}>₹{account.balance.toLocaleString("en-IN")}</p>
            <div style={{ display: "flex", gap: 32, marginBottom: 24 }}>
              {[["Account Holder", account.ownerName, false], ["Account Number", account.accountId, true]].map(([l, v, mono]) => (
                <div key={l}>
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", marginBottom: 3 }}>{l}</p>
                  <p style={{ fontSize: 13.5, fontWeight: 600, color: "white", fontFamily: mono ? "'JetBrains Mono'" : "inherit" }}>{v}</p>
                </div>
              ))}
              <div>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", marginBottom: 3 }}>Status</p>
                <span className="badge badge-active">Active</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {[["↑ Deposit", "deposit"], ["↓ Withdraw", "withdraw"]].map(([label, action]) => (
                <button key={action} onClick={() => setModal(action)} style={{ flex: 1, padding: 11, borderRadius: 9, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "white", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>{label}</button>
              ))}
            </div>
          </div>
          <div className="card">
            <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border2)" }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond'", fontSize: 17, color: "var(--navy)" }}>Transaction History</h3>
            </div>
            {history.length === 0
              ? <div style={{ padding: "36px 22px", textAlign: "center", color: "var(--text3)", fontSize: 13.5 }}>No transactions yet.</div>
              : history.map((h, i) => {
                  const isC = h.includes("Deposited") || h.includes("created");
                  const isDb = h.includes("Withdrew");
                  return (
                    <div key={i} className="tx-row">
                      <div className="tx-icon" style={{ background: isC ? "var(--green-bg)" : isDb ? "var(--red-bg)" : "var(--cream2)", color: isC ? "var(--green)" : isDb ? "var(--red)" : "var(--text2)" }}>
                        {isC ? "↑" : isDb ? "↓" : "·"}
                      </div>
                      <p style={{ flex: 1, fontSize: 13, color: "var(--text)" }}>{h}</p>
                      <span className="badge" style={{ background: isC ? "var(--green-bg)" : isDb ? "var(--red-bg)" : "var(--cream2)", color: isC ? "var(--green)" : isDb ? "var(--red)" : "var(--text2)", border: "none" }}>
                        {isC ? "Credit" : isDb ? "Debit" : "System"}
                      </span>
                    </div>
                  );
                })
            }
          </div>
        </>}
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (fu) => {
      if (fu) {
        try {
          const snap = await getDoc(doc(db, "users", fu.uid));
          setUser(snap.exists() ? { uid: fu.uid, email: fu.email, ...snap.data() } : null);
        } catch { setUser(null); }
      } else { setUser(null); }
      setBooting(false);
    });
  }, []);

  if (booting) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--navy)" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 42, height: 42, background: "var(--gold)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, margin: "0 auto 14px" }}>🏛</div>
        <p style={{ fontFamily: "'Cormorant Garamond'", fontSize: 17, color: "white", marginBottom: 6 }}>SecurBank</p>
        <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.28)" }}>Authenticating...</p>
      </div>
    </div>
  );

  if (!user) return <Login onLogin={setUser} />;
  if (user.role === "admin") return <AdminDashboard user={user} onLogout={() => signOut(auth)} />;
  return <UserDashboard user={user} onLogout={() => signOut(auth)} />;
}